import { useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

export function UpdateBanner() {
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);
  const reloadingRef = useRef(false);
  const [hasWaiting, setHasWaiting] = useState(false);

  const {
    needRefresh: [needRefresh],
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration;
    },
  });

  useEffect(() => {
    const interval = setInterval(() => {
      // update() rechaza si falla el fetch del sw.js (offline, 5xx). Sin catch sería
      // una unhandledrejection por minuto que Sentry reporta como issue.
      registrationRef.current
        ?.update()
        .then(() => {
          if (registrationRef.current?.waiting) setHasWaiting(true);
        })
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  const reload = () => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    window.location.reload();
  };

  const handleUpdateClick = async () => {
    // No alcanza con updateServiceWorker() de vite-plugin-pwa. Manda el SKIP_WAITING
    // al worker correcto (registration.waiting), pero después espera un evento
    // "controlling" de workbox-window para recargar, y ese evento nunca llega si esta
    // pestaña cargó sin controller (visita nueva, o cualquier sesión donde
    // navigator.serviceWorker.controller era null al registrar el SW): nuestro sw.js
    // NO llama self.clients.claim(), así que un worker recién activado no tapa a una
    // página que ya estaba abierta — solo controla navegaciones futuras. Sin
    // clients.claim(), "controllerchange" jamás dispara para esa pestaña, así que el
    // botón queda muerto para siempre (verificado: reg.waiting pasa a null tras el
    // SKIP_WAITING, pero navigator.serviceWorker.controller se queda en null 8s
    // después del click). Por eso acá se postea el mensaje directo Y se fuerza la
    // recarga con un timeout si controllerchange no llega — el timeout no es una
    // red de seguridad opcional, es el único camino que funciona en ese caso.
    const registration =
      registrationRef.current ?? (await navigator.serviceWorker?.getRegistration());
    const waiting = registration?.waiting;

    if (!waiting) {
      reload();
      return;
    }

    navigator.serviceWorker.addEventListener("controllerchange", reload, { once: true });
    waiting.postMessage({ type: "SKIP_WAITING" });
    // Red de seguridad: si por lo que sea no llega el controllerchange, recargar igual.
    setTimeout(reload, 3000);
  };

  if (!needRefresh && !hasWaiting) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-4 bg-neutral-950 px-4 py-4 text-sm font-medium text-white"
      style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
    >
      <span>Hay una versión nueva disponible</span>
      <button
        onClick={handleUpdateClick}
        className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 active:bg-primary/80"
      >
        Actualizar ahora
      </button>
    </div>
  );
}
